import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, 
  Clock,
  Users,
  Video,
  Phone,
  MoreHorizontal,
  Trash2,
  CheckCircle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CalendarPage() {
  const [meetings, setMeetings] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    title: '',
    date: '',
    time: '10:00',
    duration_minutes: 60,
    meeting_type: 'monthly_review',
    notes: '',
    status: 'scheduled'
  });

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [meetingsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/meetings`, { withCredentials: true }),
        axios.get(`${API}/clients`, { withCredentials: true })
      ]);
      setMeetings(meetingsRes.data);
      setClients(clientsRes.data);
    } catch (e) {
      toast.error('Erreur lors du chargement du calendrier');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dateTime = new Date(`${formData.date}T${formData.time}:00`);
      await axios.post(`${API}/meetings`, {
        ...formData,
        date: dateTime.toISOString()
      }, { withCredentials: true });
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la creation');
    }
  };

  const handleDelete = async (meetingId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet entretien ?')) return;
    try {
      await axios.delete(`${API}/meetings/${meetingId}`, { withCredentials: true });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleComplete = async (meetingId) => {
    try {
      await axios.put(`${API}/meetings/${meetingId}`, {
        ...meetings.find(m => m.id === meetingId),
        status: 'completed'
      }, { withCredentials: true });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de mise a jour');
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      title: '',
      date: '',
      time: '10:00',
      duration_minutes: 60,
      meeting_type: 'monthly_review',
      notes: '',
      status: 'scheduled'
    });
  };

  const getMeetingsForDate = (date) => {
    return meetings.filter(m => {
      const meetingDate = new Date(m.date);
      return meetingDate.toDateString() === date.toDateString();
    });
  };

  const selectedDateMeetings = getMeetingsForDate(selectedDate);

  const meetingDates = meetings.map(m => new Date(m.date).toDateString());

  const calendarModifiers = useMemo(() => ({
    hasMeeting: (date) => meetingDates.includes(date.toDateString())
  }), [meetingDates]);

  const calendarModifierStyles = useMemo(() => ({
    hasMeeting: { backgroundColor: '#002FA7', color: 'white', fontWeight: 'bold' }
  }), []);

  // Sorted full meetings list — memoized to avoid re-sorting on every render
  const sortedMeetings = useMemo(
    () => meetings.slice().sort((a, b) => new Date(a.date) - new Date(b.date)),
    [meetings],
  );

  const meetingTypeIcons = {
    monthly_review: <Users className="w-4 h-4" />,
    video_call: <Video className="w-4 h-4" />,
    phone_call: <Phone className="w-4 h-4" />
  };

  const meetingTypeLabels = {
    monthly_review: 'Bilan mensuel',
    video_call: 'Appel vidéo',
    phone_call: 'Appel téléphonique'
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="calendar-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">
              Calendrier
            </h1>
            <p className="text-neutral-500 mt-1">Planifiez vos entretiens clients</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#002FA7] hover:bg-[#002277] text-white"
                data-testid="add-meeting-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Planifier un entretien
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">Nouvel entretien</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="title">Titre *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: Bilan mensuel SEO"
                    required
                    className="mt-1"
                    data-testid="meeting-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="client">Client *</Label>
                    <Select
                      value={formData.client_id}
                      onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                      required
                    >
                      <SelectTrigger className="mt-1" data-testid="meeting-client-select">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="meeting_type">Type d'entretien</Label>
                    <Select
                      value={formData.meeting_type}
                      onValueChange={(value) => setFormData({ ...formData, meeting_type: value })}
                    >
                      <SelectTrigger className="mt-1" data-testid="meeting-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly_review">Bilan mensuel</SelectItem>
                        <SelectItem value="video_call">Appel vidéo</SelectItem>
                        <SelectItem value="phone_call">Appel téléphonique</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      className="mt-1"
                      data-testid="meeting-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="time">Heure *</Label>
                    <Input
                      id="time"
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      required
                      className="mt-1"
                      data-testid="meeting-time-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="duration">Durée (min)</Label>
                    <Select
                      value={String(formData.duration_minutes)}
                      onValueChange={(value) => setFormData({ ...formData, duration_minutes: parseInt(value) })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 min</SelectItem>
                        <SelectItem value="45">45 min</SelectItem>
                        <SelectItem value="60">1 heure</SelectItem>
                        <SelectItem value="90">1h30</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Points à aborder..."
                    className="mt-1"
                    rows={3}
                    data-testid="meeting-notes-input"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-[#002FA7] hover:bg-[#002277]"
                    data-testid="save-meeting-btn"
                  >
                    Planifier
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-2 bg-white border border-neutral-200 p-6" data-testid="calendar-widget">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              locale={fr}
              className="rounded-md"
              modifiers={calendarModifiers}
              modifiersStyles={calendarModifierStyles}
            />
          </div>

          {/* Selected Date Meetings */}
          <div className="bg-white border border-neutral-200" data-testid="day-meetings">
            <div className="p-4 border-b border-neutral-200">
              <h2 className="font-heading font-semibold text-lg text-neutral-900">
                {format(selectedDate, 'EEEE d MMMM', { locale: fr })}
              </h2>
              <p className="text-sm text-neutral-500">
                {selectedDateMeetings.length} entretien{selectedDateMeetings.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="divide-y divide-neutral-100">
              {selectedDateMeetings.length === 0 ? (
                <div className="p-8 text-center">
                  <Clock className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
                  <p className="text-neutral-500 text-sm">Aucun entretien prévu</p>
                </div>
              ) : (
                selectedDateMeetings.map((meeting) => (
                  <div key={meeting.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded flex items-center justify-center ${
                          meeting.status === 'completed' 
                            ? 'bg-green-50 text-green-600' 
                            : 'bg-[#002FA7]/10 text-[#002FA7]'
                        }`}>
                          {meetingTypeIcons[meeting.meeting_type]}
                        </div>
                        <div>
                          <p className="font-medium text-neutral-900">{meeting.title}</p>
                          <p className="text-sm text-neutral-500">{meeting.client_name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="font-mono text-xs text-neutral-500">
                              {format(new Date(meeting.date), 'HH:mm')}
                            </span>
                            <span className="text-neutral-300">-</span>
                            <span className="font-mono text-xs text-neutral-500">
                              {meeting.duration_minutes} min
                            </span>
                          </div>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {meeting.status !== 'completed' && (
                            <DropdownMenuItem onClick={() => handleComplete(meeting.id)}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Marquer terminé
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={() => handleDelete(meeting.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    {meeting.notes && (
                      <p className="text-sm text-neutral-500 mt-2 ml-11">{meeting.notes}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Upcoming Meetings List */}
        <div className="bg-white border border-neutral-200" data-testid="all-meetings">
          <div className="p-4 border-b border-neutral-200">
            <h2 className="font-heading font-semibold text-lg text-neutral-900">
              Tous les entretiens
            </h2>
          </div>
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin mx-auto" />
            </div>
          ) : meetings.length === 0 ? (
            <div className="p-12 text-center">
              <Clock className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
              <p className="text-neutral-500">Aucun entretien planifié</p>
            </div>
          ) : (
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th>Entretien</th>
                  <th>Client</th>
                  <th>Date</th>
                  <th>Durée</th>
                  <th>Type</th>
                  <th>Statut</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {sortedMeetings.map((meeting) => (
                  <tr key={meeting.id}>
                    <td className="font-medium text-neutral-900">{meeting.title}</td>
                    <td className="text-neutral-600">{meeting.client_name}</td>
                    <td>
                      <span className="font-mono text-sm">
                        {format(new Date(meeting.date), 'dd/MM/yyyy HH:mm')}
                      </span>
                    </td>
                    <td className="font-mono text-sm text-neutral-600">{meeting.duration_minutes} min</td>
                    <td className="text-neutral-600">{meetingTypeLabels[meeting.meeting_type]}</td>
                    <td>
                      <span className={`text-xs font-mono px-2 py-1 rounded ${
                        meeting.status === 'completed' 
                          ? 'bg-green-50 text-green-700' 
                          : meeting.status === 'cancelled'
                          ? 'bg-red-50 text-red-700'
                          : 'bg-[#002FA7]/10 text-[#002FA7]'
                      }`}>
                        {meeting.status === 'completed' ? 'Terminé' : meeting.status === 'cancelled' ? 'Annulé' : 'Planifié'}
                      </span>
                    </td>
                    <td>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {meeting.status !== 'completed' && (
                            <DropdownMenuItem onClick={() => handleComplete(meeting.id)}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Marquer terminé
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={() => handleDelete(meeting.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
